/**
 * One source for system labels on every client surface, so a game never
 * reads "ATARI2600" in one place and "ATARI 2600" in another (homepage
 * review follow-ups, docs/superpowers/specs/2026-09-25-home-review-quick-fixes-design.md,
 * section 2).
 *
 *   systemTag(id)  - the short caps tag for chips, cards and meta lines:
 *                    "NES", "GB", "ATARI 2600".
 *   systemName(id) - the display name for filters, headings and footers,
 *                    from the SYSTEMS registry: "Game Boy", "Commodore 64".
 *
 * Both take a catalog system id in any case. Client code calls these instead
 * of uppercasing an id itself.
 *
 * Importing src/client/cores/systems.js from src/shared is safe: the Docker
 * runtime image ships that one client file, and
 * src/shared/shipped-client-imports.test.js enforces it.
 */
import { SYSTEMS } from '../client/cores/systems.js';

// One short caps tag per catalog id (gbc is its own catalog id even though
// it shares the Game Boy core). A Map, so an id such as "constructor" can
// never read a label off Object.prototype.
const SYSTEM_TAGS = new Map([
  ['nes', 'NES'],
  ['snes', 'SNES'],
  ['gb', 'GB'],
  ['gbc', 'GBC'],
  ['gba', 'GBA'],
  ['atari2600', 'ATARI 2600'],
  ['c64', 'C64'],
  ['genesis', 'GENESIS'],
  ['n64', 'N64'],
]);

const normalize = (id) => String(id || '').toLowerCase();

/**
 * Chip/meta tag for a system id: "NES", "GB", "ATARI 2600". An unknown id
 * reads as its uppercase form; an empty id reads as ''.
 */
export function systemTag(id) {
  const key = normalize(id);
  return SYSTEM_TAGS.get(key) ?? key.toUpperCase();
}

/**
 * Display name for a system id, from SYSTEMS[id].name: "Game Boy",
 * "Commodore 64". gbc resolves to Game Boy through the registry's alias. An
 * id the registry doesn't know falls back to systemTag(id).
 */
export function systemName(id) {
  const key = normalize(id);
  return (Object.hasOwn(SYSTEMS, key) && SYSTEMS[key].name) || systemTag(key);
}
